import React, { useState } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  VStack,
  Tag,
  Avatar,
  Text,
  Flex,
  useDisclosure,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useColorModeValue,
} from "@chakra-ui/react";
import { ChatBubbleLeftRightIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { CiEdit } from "react-icons/ci";
import { MdDeleteOutline } from "react-icons/md";
import { PiUsersThreeBold } from "react-icons/pi";
import { ChakraIcon } from "./ChakraIcon";
import { useColorMode } from "@chakra-ui/react";
import { getThemeClasses } from "../utils/themeUtils";
import { getProjectPermissions, getProjectScopeAccess } from "../utils/permissions";

// Define Project Type
type Project = {
  id: string;
  name: string;
  description?: string;
  channels: string[];
  assignedUsers?: string[];
  status?: string;
  createdBy?: string;
};

// Define User Type
interface User {
  id: string;
  firstName: string;
  lastName: string;
}

// Props for ProjectTable
interface ProjectTableProps {
  filteredProjects: Project[];
  users: User[];
  handleEditProject: (project: Project) => void;
  confirmDelete: (projectId: string) => void;
  textColor: string;
  cardBg: string;
  currentUser: any;
  /** Whether the Access column is shown (admins manage project-scoped roles). */
  canManageAccess?: boolean;
  handleOpenProjectAccess?: (project: Project) => void;
}

const ProjectTable: React.FC<ProjectTableProps> = ({
  filteredProjects,
  users,
  handleEditProject,
  confirmDelete,
  textColor,
  cardBg,
  currentUser,
  canManageAccess = false,
  handleOpenProjectAccess,
}) => {
  const projectPerms = React.useMemo(
    () => getProjectPermissions(currentUser),
    [currentUser]
  );
  const { canDelete: canDeleteProject } = projectPerms;

  // Editing a project can also be granted per project (admin/editor grants),
  // on top of whatever the org-wide permission already allows.
  const canEditProject = React.useCallback(
    (project: Project | null | undefined) =>
      projectPerms.canEdit(project) ||
      getProjectScopeAccess(currentUser, project?.id).canEditProject,
    [projectPerms, currentUser]
  );

  const showAccessColumn = canManageAccess && !!handleOpenProjectAccess;

  // Actions column is always visible when there are projects — the edit icon
  // shows for every row and the handler decides whether to open the form or a popup.
  const showActionsColumn = filteredProjects.length > 0;
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [modalType, setModalType] = useState<"channels" | "users">("channels");

  const openDetailsModal = (project: Project, type: "channels" | "users") => {
    setSelectedProject(project);
    setModalType(type);
    onOpen();
  };

  const headerBg = useColorModeValue("#F9FAFB", "#14161B");
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);

  return (
    <Box display="flex" flexDirection="column" overflow="hidden" pb={1}>
      <Box
        className={`${isDark ? 'bg-[#1A1C1F] border border-[#2E2F33] shadow-[0_6px_24px_rgba(0,0,0,0.35)]' : 'bg-white border border-gray-200 shadow-sm'}`}
        borderRadius="md"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        {/* Scrollable Table Area */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden [scrollbar-gutter:stable]" style={{ maxHeight: "68.2vh" }}>
          <Table bg={cardBg} variant="simple" size={"sm"} width={"100%"} mb={1}>
            <Thead
              position="sticky"
              top={0}
              zIndex={1}
              bg={headerBg}
              color={textColor}
            >
              <Tr color={isDark ? 'white' : 'gray.700'} bg={headerBg}>
                <Th py={"3"} width={"30px"}>
                  S.No
                </Th>
                <Th py={"3"} minW="150px">Project Name</Th>
                <Th py={"3"} textAlign="center">Channels</Th>
                <Th py={"3"} textAlign="center">Assigned Users</Th>
                {showAccessColumn && <Th py={"3"} textAlign="center">Access</Th>}
                {showActionsColumn && <Th py={"3"} textAlign="center">Actions</Th>}
              </Tr>
            </Thead>
            <Tbody color={textColor}>
              {filteredProjects.length > 0 ? (
                filteredProjects.map((project, index) => {
                  const canEditThis = canEditProject(project);
                  const canDeleteThis = canDeleteProject(project);
                  return (
                    <Tr
                      key={project.id}
                      _hover={{ bg: isDark ? "#2A3441" : "#F2F4F7" }}
                      borderBottomWidth="1px"
                      borderColor={isDark ? "#2E2F33" : "#E5E7EB"}
                    >
                      <Td py={"3"} maxW="50px" whiteSpace="nowrap">
                        {index + 1}
                      </Td>

                      {/* Project Name */}
                      <Td
                        maxW="200px"
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                      >
                        {project.name}
                      </Td>

                      {/* Channels - Click to Open Modal */}
                      <Td maxW="100px" textAlign={"center"}>
                        <Flex justifyContent="center">
                          <button
                            onClick={() => openDetailsModal(project, "channels")}
                            aria-label="View Channels"
                            className={`p-1 rounded-full ${isDark ? 'text-[#B59143] hover:bg-[#2A3441] focus:ring-[#B59143]' : 'text-[#B59143] hover:bg-[#FFF7E6] focus:ring-[#B59143]'} focus:outline-none focus:ring-2 focus:ring-opacity-50`}
                          >
                            <ChatBubbleLeftRightIcon className="h-4 w-4" />
                          </button>
                        </Flex>
                      </Td>

                      {/* Assigned Users - Click to Open Modal */}
                      <Td maxW={"100px"} textAlign={"center"}>
                        <Flex justifyContent="center">
                          <button
                            onClick={() => openDetailsModal(project, "users")}
                            aria-label="View Assigned Users"
                            className={`p-1 rounded-full ${isDark ? 'text-[#B59143] hover:bg-[#2A3441] focus:ring-[#B59143]' : 'text-[#B59143] hover:bg-[#FFF7E6] focus:ring-[#B59143]'} focus:outline-none focus:ring-2 focus:ring-opacity-50`}
                          >
                            <ChakraIcon as={PiUsersThreeBold} boxSize={4} />
                          </button>
                        </Flex>
                      </Td>

                      {/* Access - per-project admin/editor/viewer roles */}
                      {showAccessColumn && (
                        <Td maxW={"100px"} textAlign={"center"}>
                          <Flex justifyContent="center">
                            <button
                              onClick={() => handleOpenProjectAccess?.(project)}
                              aria-label={`Manage access for ${project.name}`}
                              title="Manage project access"
                              className={`p-1 rounded-full ${isDark ? 'text-[#B59143] hover:bg-[#2A3441] focus:ring-[#B59143]' : 'text-[#B59143] hover:bg-[#FFF7E6] focus:ring-[#B59143]'} focus:outline-none focus:ring-2 focus:ring-opacity-50`}
                            >
                              <ShieldCheckIcon className="h-4 w-4" />
                            </button>
                          </Flex>
                        </Td>
                      )}

                      {/* Actions - shown for every project row */}
                      <Td className="px-2 py-2 whitespace-nowrap text-sm font-medium" textAlign="center">
                          <Flex justifyContent="center" gap={1}>
                            <button
                              onClick={() => handleEditProject(project)}
                              className={`inline-flex items-center p-1 rounded-full ${isDark ? 'text-[#B59143] hover:bg-[#2A3441] focus:ring-[#B59143]' : 'text-[#B59143] hover:bg-[#FFF7E6] focus:ring-[#B59143]'} focus:outline-none focus:ring-2 focus:ring-opacity-50`}
                              aria-label={`Edit ${project.name}`}
                            >
                              <ChakraIcon as={CiEdit} boxSize={4} />
                            </button>
                            {canDeleteThis && (
                              <button
                                onClick={() => confirmDelete(project.id)}
                                className={`inline-flex items-center p-1 rounded-full ${isDark ? 'text-[#B59143] hover:bg-[#2A3441] focus:ring-[#B59143]' : 'text-[#B59143] hover:bg-[#FFF7E6] focus:ring-[#B59143]'} focus:outline-none focus:ring-2 focus:ring-opacity-50`}
                                aria-label={`Delete ${project.name}`}
                              >
                                <ChakraIcon as={MdDeleteOutline} boxSize={4} />
                              </button>
                            )}
                          </Flex>
                        </Td>
                    </Tr>
                  );
                })
              ) : (
                <Tr>
                  <Td
                    colSpan={4 + (showAccessColumn ? 1 : 0) + (showActionsColumn ? 1 : 0)}
                    textAlign="center"
                    py={5}
                    fontWeight="bold"
                    color="gray.500"
                    fontSize={"sm"}
                  >
                    <Text fontSize="sm" color="gray.500">
                      No projects found.
                    </Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </div>
      </Box>

      {/* ✅ Modal for Viewing Channels or Assigned Users */}
      {selectedProject && (
        <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
          <ModalOverlay />
          <ModalContent borderRadius="lg" p={5} boxShadow="xl">
            <ModalHeader fontSize="lg" fontWeight="bold" color={textColor}>
              {modalType === "channels" ? "Channels" : "Assigned Users"} -{" "}
              {selectedProject.name}
            </ModalHeader>
            <ModalCloseButton color={textColor} />

            <ModalBody maxH="300px" overflowY="auto" color={textColor}>
              {/* Channels List */}
              {modalType === "channels" && (
                <VStack align="start" spacing={3}>
                  {selectedProject.channels.length > 0 ? (
                    <HStack flexWrap="wrap">
                      {selectedProject.channels.map((channel, idx) => (
                        <Tag
                          key={idx}
                          size="sm"
                          bg={isDark ? "transparent" : "#FFF7E6"}
                          color={isDark ? "#FBD282" : "#B59143"}
                          borderWidth="1px"
                          borderColor={isDark ? "#B59143" : "#E8C16C"}
                          borderRadius="full"
                          px={2}
                          py={1}
                        >
                          {channel}
                        </Tag>
                      ))}
                    </HStack>
                  ) : (
                    <Text fontSize="sm" color="gray.500">
                      No Channels Assigned
                    </Text>
                  )}
                </VStack>
              )}

              {/* Assigned Users List */}
              {modalType === "users" && (
                <VStack align="start" spacing={3}>
                  {selectedProject.assignedUsers &&
                    selectedProject.assignedUsers.length > 0 ? (
                    selectedProject.assignedUsers.map((userId) => {
                      const user = users.find((u) => u.id === userId);
                      return user ? (
                        <HStack key={user.id}>
                          <Avatar
                            name={`${user.firstName} ${user.lastName}`}
                            size="xs"
                          />
                          <Text fontSize="sm">
                            {user.firstName} {user.lastName}
                          </Text>
                        </HStack>
                      ) : (
                        <Text key={userId} color="red.500" fontSize="sm"></Text>
                      );
                    })
                  ) : (
                    <Text fontSize="sm" color="gray.500">
                      No Users Assigned
                    </Text>
                  )}
                </VStack>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
};

export default ProjectTable;
